import { RegionId } from "@sp-crm/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import {
    AddReferralPipelineSection,
    ReferralPipelineSettings,
} from "components/manage/agency-settings/agency-settings-referral-pipelines";
import { PipelineSummary } from "components/referral-pipelines/types";
import { trpc } from "components/rpc/trpc";
import { ErrorMessage } from "components/ui/error-message";
import { InternalLink } from "components/ui/internal-link";
import { Spinner } from "components/ui/spinner";
import React, { useCallback } from "react";
import { useCurrentRegion, useRegionId } from "store/selectors/hooks";

export const RegionSettingsReferralPipelines: React.FC<unknown> = () => {
    const region = useCurrentRegion();
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();

    const pipelinesQuery = useQuery({
        ...trpcClient.pipeline.list.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: !!regionId,
    });

    const refetchPipelines = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [["pipeline"]] });
    }, [queryClient]);

    if (!regionId) {
        return null;
    }
    if (pipelinesQuery.isPending) {
        return <Spinner />;
    }
    if (pipelinesQuery.isError) {
        return (
            <ErrorMessage component="RegionSettingsReferralPipelines">
                <pre>{JSON.stringify(pipelinesQuery.error, null, 2)}</pre>
            </ErrorMessage>
        );
    }

    const pipelines = pipelinesQuery.data ?? [];
    const agencyPipelines = pipelines.filter(pipeline => pipeline.scope === "agency");
    const officePipelines = pipelines.filter(pipeline => pipeline.scope === "office");

    return (
        <ContentFull>
            <ContentHeader>{`Referral pipelines - ${region?.name}`}</ContentHeader>
            <ContentSubheader>
                Pipelines are boards where referral contacts and referral organizations
                move through stages.
            </ContentSubheader>
            <div className="space-y-4">
                {agencyPipelines.length > 0 ? (
                    <div>
                        <h3 className="text-lg font-medium text-gray-900">
                            Agency pipelines
                        </h3>
                        <p className="text-sm text-gray-500">
                            These pipelines apply to every office and are managed in the
                            agency settings.
                        </p>
                        <ul className="mt-2 divide-y divide-gray-200">
                            {agencyPipelines.map(pipeline => (
                                <AgencyPipelineSummary
                                    key={pipeline.id}
                                    pipeline={pipeline}
                                />
                            ))}
                        </ul>
                    </div>
                ) : null}
                <div>
                    <h3 className="text-lg font-medium text-gray-900">
                        {`Pipelines for ${region?.name}`}
                    </h3>
                    <p className="text-sm text-gray-500">
                        These pipelines are specific to {region?.name} and only appear
                        when this office is selected.
                    </p>
                    <div className="mt-2 divide-y divide-gray-200">
                        {officePipelines.map(pipeline => (
                            <ReferralPipelineSettings
                                key={pipeline.id}
                                pipeline={pipeline}
                                scope={{ kind: "office", regionId }}
                                onChanged={refetchPipelines}
                            />
                        ))}
                        <AddReferralPipelineSection
                            scope={{ kind: "office", regionId }}
                            heading={`Add referral pipeline for ${region?.name}`}
                            onCreated={refetchPipelines}
                        />
                    </div>
                </div>
            </div>
        </ContentFull>
    );
};

const AgencyPipelineSummary: React.FC<{ pipeline: PipelineSummary }> = props => {
    const { pipeline } = props;
    return (
        <li className="py-3 flex items-center justify-between">
            <div>
                <p className="font-medium text-gray-900">{pipeline.name}</p>
                <p className="text-sm text-gray-500">
                    {pipeline.stages.length > 0
                        ? pipeline.stages.map(stage => stage.name).join(" → ")
                        : "No stages yet"}
                </p>
            </div>
            <InternalLink to={`/pipelines/${pipeline.id}`} className="text-sm">
                View pipeline
            </InternalLink>
        </li>
    );
};
